import { useCallback, useEffect, useRef, useState } from 'react'
import { useGetTopics, useGetTopicDetails } from '@/hooks/api/dashboard'
import { TopicType } from '@/utils/constants'
import { STUDIO3D_NOTE_NAME } from '@/features/gallery-studio-3d/constants'
import type { StudioSceneState } from '@/features/gallery-studio-3d/types'
import { topicToGallerySummary } from '../mappers'
import { GallerySummary } from '../types'

function parseSceneState(topic: { notes?: { noteName?: string; noteText?: string }[] } | null | undefined): StudioSceneState | null {
  const note = topic?.notes?.find((n) => n.noteName === STUDIO3D_NOTE_NAME)
  if (!note?.noteText) return null
  try {
    const parsed = JSON.parse(note.noteText)
    if (parsed?.version === 1 && Array.isArray(parsed.arts)) return parsed as StudioSceneState
    return null
  } catch {
    return null
  }
}

/**
 * Loads the user's GALLERY topics, then progressively enriches each card
 * with its real `studio3dSceneStateV1` note (room mode/preset/colors, the
 * actual hung prints) — one `getTopicDetails` call per gallery, run in
 * parallel and merged in as each settles so the grid renders immediately
 * off the cheap list call instead of blocking on all of them. Same
 * per-gallery request cost `useStudio3DData` already pays to open a single
 * gallery in Studio; acceptable for a personal-library-sized list.
 *
 * Cards render off the list call alone until their enrichment resolves —
 * `sceneLoaded` flips once it has, whether or not a saved note existed.
 */
export function useGalleryLibrary() {
  const getTopics = useGetTopics()
  const getTopicDetails = useGetTopicDetails()
  const [galleries, setGalleries] = useState<GallerySummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const generationRef = useRef(0)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    const generation = ++generationRef.current
    try {
      const topics = await getTopics.mutateAsync({ pageNo: 1, interaction: 3, topicType: TopicType.GALLERY })
      const list: GallerySummary[] = (topics || []).map(
        (t: Parameters<typeof topicToGallerySummary>[0]) => topicToGallerySummary(t),
      )
      if (generationRef.current !== generation) return
      setGalleries(list)
      setLoading(false)

      list.forEach((g) => {
        getTopicDetails
          .mutateAsync({ topicID: g.id })
          .then((topicDetail) => {
            if (generationRef.current !== generation) return
            const scene = parseSceneState(topicDetail)
            setGalleries((prev) =>
              prev.map((x) => {
                if (x.id !== g.id) return x
                if (!scene) return { ...x, sceneLoaded: true }
                return {
                  ...x,
                  mode: scene.room.mode || 'studio',
                  scenePreset: scene.room.scenePreset || 'corridor',
                  classicTemplateId: scene.room.classicTemplateId ?? x.classicTemplateId,
                  wallColor: scene.room.wallColor,
                  floorColor: scene.room.floorColor,
                  printCount: scene.arts.length || x.printCount,
                  arts: scene.arts.slice(0, 3).map((a) => ({ img: a.img, ratio: a.ratio })),
                  sceneLoaded: true,
                }
              }),
            )
          })
          .catch(() => {
            if (generationRef.current !== generation) return
            setGalleries((prev) => prev.map((x) => (x.id === g.id ? { ...x, sceneLoaded: true } : x)))
          })
      })
    } catch (e) {
      if (generationRef.current !== generation) return
      setError(e instanceof Error ? e.message : 'Failed to load galleries')
      setGalleries([])
      setLoading(false)
    }
  }, [getTopics, getTopicDetails])

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { galleries, loading, error, refresh }
}
